import { useTheme } from '@mui/material/styles';
import type * as ChartJs from 'chart.js';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { SignType } from '@constants/fhir';
import { getObservationConfig, VITAL_SIGN_TYPES } from '@constants/fhir';

import { useConversion } from '@hooks/useConversion';
import { useHealthChart } from '@hooks/useHealthChart';
import { useMetricWithinRange } from '@hooks/useMetricWithinRange';
import useReferenceRange from '@hooks/useReferenceRange';

import type { DataPointMapped } from '@models/data-point';
import { FRIENDLY_CODE_NAME_MAP } from '@models/graph';

import {
  determineXTimeInput,
  getBoundaryValues,
  groupByIntervalAndAverage,
} from '@utils/chart';
import { convertUnit, getUnit } from '@utils/conversion';

import type { ChartData, LineChartColorOptions, Plugin } from './LineChart';
import LineChart from './LineChart';

const messages = defineMessages({
  timeUnknownDisclaimer: {
    id: 'medicalChart.timeUnknownDisclaimer',
    defaultMessage:
      'All measurements will be averaged (including clinical and non-clinical) when viewing week, month, or year intervals. Daily measurements will be averaged if there are more than one from that day. If the time of the measurement is unknown it will be set to midnight.',
  },
  low: {
    id: 'medicalChart.stressStatus.low',
    defaultMessage: 'Low',
  },
  normal: {
    id: 'medicalChart.stressStatus.normal',
    defaultMessage: 'Normal',
  },
  elevated: {
    id: 'medicalChart.stressStatus.elevated',
    defaultMessage: 'Elevated',
  },
  high: {
    id: 'medicalChart.stressStatus.high',
    defaultMessage: 'High',
  },
});

interface HealthLineChartProps {
  code: string;
  data: DataPointMapped[];
  options?: HealthLineChartOptions;
}

interface HealthLineChartOptions {
  showNormalRange?: boolean;
  colors?: LineChartColorOptions;
}

type Dataset = ChartJs.ChartDataset<'line', StyledDataPoint[]>;

export type StyledDataPoint = ChartJs.Point & {
  pointStyle: ChartJs.PointStyle;
};

function defaultTransformation(data: DataPointMapped[]): Dataset {
  return {
    data: data.map((dataPoint) => ({
      y: dataPoint.value,
      x: new Date(dataPoint.date ?? 0).getTime(),
      pointStyle: dataPoint.alert ? false : 'circle',
    })),
    label: '-',
    pointStyle: 'circle',
    pointRadius: 4,
  };
}

function getCanvasAlert(color: string): ChartJs.PointStyle {
  const cvs = document.createElement('canvas');
  const ctx = cvs.getContext('2d');
  if (!ctx) {
    return 'circle';
  }
  const radius = 10;
  cvs.height = 2 * radius;
  cvs.width = 2 * radius;

  // Triangle coordinates
  const { x1, x2, x3 } = {
    x1: 0,
    x2: cvs.width,
    x3: cvs.width / 2,
  };
  const { y1, y2, y3 } = { y1: cvs.height, y2: cvs.height, y3: 0 };

  // Draw the triangle
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x3, y3);
  ctx.closePath();
  ctx.stroke();
  ctx.fill();

  // Text properties
  ctx.font = '15px Arial';
  ctx.fillStyle = 'white';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Calculate the center of the triangle for the exclamation mark
  const centerX = (x1 + x2 + x3) / 3;
  const centerY = (y1 + y2 + y3) / 3;

  // Draw the exclamation mark
  ctx.fillText('!', centerX, centerY);

  return cvs;
}

function HealthLineChart({ code, data, options }: HealthLineChartProps) {
  const { formatMessage } = useIntl();
  const { interval, maxDate, minDate, normalRange, referenceRange } =
    useHealthChart();
  const { parameterMap } = useReferenceRange();
  const { determineMetricRange } = useMetricWithinRange();
  const { palette } = useTheme();
  const {
    state: { conversion },
  } = useConversion();

  const signType = getObservationConfig(code);

  // `format` needs the *display* unit to pick its representation - without it
  // body height falls back to decimal inches ("68.11") instead of "5' 8"".
  const displayUnit = getUnit(
    signType?.unit,
    conversion[code] ? 'CON' : 'SI',
    code
  );
  const formatChartValue = signType?.format
    ? (value: number) => signType.format?.(value, displayUnit) ?? String(value)
    : undefined;

  const chartDataDeps: React.DependencyList = [
    code,
    JSON.stringify(data),
    JSON.stringify(parameterMap),
    JSON.stringify(referenceRange?.risks),
    minDate.toISOString(),
    maxDate.toISOString(),
  ];

  const chartData: ChartData = React.useMemo<ChartData>(() => {
    let datasets: Dataset[];
    let codes: string[];
    if (signType) {
      // Dataset indexes align with sign type keys
      datasets = transformToGraphDatasets(signType, data);
      codes = signType.keys.map(({ common }) => common);
    } else {
      datasets = [defaultTransformation(data)];
      codes = [code];
    }
    const hasMoreThanOneRefRange =
      referenceRange?.risks && referenceRange.risks.length > 1;

    return {
      datasets: datasets.map((dataset, datasetIndex) => {
        const datapoints = groupByIntervalAndAverage(
          dataset.data,
          minDate,
          maxDate
        );

        const parameterCode = codes[datasetIndex] ?? code;
        const backgroundColor = hasMoreThanOneRefRange
          ? datapoints.map((point) => {
              const range = determineMetricRange(point.y || 0, parameterCode);
              return range ? parameterMap[range].colour : palette.purple.main;
            })
          : palette.purple.main;

        return {
          ...dataset,
          // If one data point within the averaged group has an alert, show alert icon
          pointStyle: datapoints.map(({ pointStyle }) =>
            pointStyle === false
              ? getCanvasAlert(backgroundColor.at(0) ?? palette.purple.main)
              : pointStyle
          ),
          data: datapoints.map(({ x, y }) => ({ x, y })),
          backgroundColor,
          // If no risks or 1 risk, use purple colour
          borderColor: hasMoreThanOneRefRange
            ? (context) => {
                const chart = context.chart;
                const { ctx, chartArea, scales } = chart;
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (!chartArea) {
                  // This case happens on initial chart load
                  return;
                }

                // Get all parameter values for the displayed dataset
                const values = referenceRange.risks
                  .flatMap(({ parameters }) =>
                    parameters
                      .filter(({ code }) => code === parameterCode || !code)
                      .flatMap(({ lower_bound, upper_bound }) => [
                        lower_bound?.value,
                        upper_bound?.value,
                      ])
                  )
                  .filter((v): v is number => v !== undefined);

                // Get min/max of parameters
                let minValue = Math.min(...values);
                let maxValue = Math.max(...values);

                // Special cases
                if (minValue === maxValue) {
                  minValue -= minValue / 2;
                  maxValue += minValue / 2;
                }

                // Determine bounds of gradient on canvas
                const yScale = scales.y;
                const datasetTop = yScale.getPixelForValue(maxValue);
                const datasetBottom = yScale.getPixelForValue(minValue);
                const gradient = ctx.createLinearGradient(
                  0,
                  datasetBottom,
                  0,
                  datasetTop
                );

                const positions: [number | null, number | null, string][] = [];
                // Populate the gradient with risk colours
                referenceRange.risks.forEach(({ parameters, risk }) => {
                  const parameter =
                    parameters.find(({ code }) => code === parameterCode) ??
                    parameters.at(datasetIndex);
                  if (!parameter) {
                    return;
                  }
                  const { lower_bound, upper_bound } = parameter;
                  if (!lower_bound && !upper_bound) {
                    return;
                  }
                  const { colour } = parameterMap[risk];

                  // Normalize the parameter bounds to a value between 0 and 1
                  const lowerPos = lower_bound
                    ? (lower_bound.value - minValue) / (maxValue - minValue)
                    : null;
                  const upperPos = upper_bound
                    ? (upper_bound.value - minValue) / (maxValue - minValue)
                    : null;

                  positions.push([lowerPos, upperPos, colour]);
                });
                positions.forEach((pos, i, arr) => {
                  const [, , colour] = pos;
                  let [lowerPos, upperPos] = pos;
                  const hasNext = i + 1 < arr.length;
                  const hasPrev = i - 1 > 0;
                  const next = hasNext ? arr[i + 1] : null;
                  const prev = hasPrev ? arr[i - 1] : null;

                  if (lowerPos !== null && upperPos !== null) {
                    // y0 < x < y1
                    // Do nothing
                  } else if (upperPos !== null) {
                    // x < y1
                    // Use prev's upper pos as lower, fallback to 0
                    lowerPos = prev?.[1] ?? 0;
                  } else if (lowerPos !== null) {
                    // y0 < x
                    // Use next's lower pos as upper, fallback to 1
                    upperPos = next?.[0] ?? 1;
                  }
                  const avgPos =
                    ((lowerPos as number) + (upperPos as number)) / 2;
                  // Add gradient stops
                  gradient.addColorStop(avgPos, colour);
                });

                return gradient;
              }
            : palette.purple.main,
        };
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, chartDataDeps);

  function transformToGraphDatasets(
    signType: SignType,
    data: DataPointMapped[]
  ): Dataset[] {
    return signType.keys.map(({ common }, index) => {
      const filteredData = data.filter(
        (dataPoint) => dataPoint.type === common && !isNaN(dataPoint.value)
      );

      const determinePointStyle = (dataPoint: DataPointMapped) => {
        const hasAlert = Boolean(dataPoint.alert);
        /**
         * Metrics with alerts
         *
         * | type            | dataPoint.alert?.codes | alertCodes         |   Result  |
         * |-----------------|------------------------|--------------------|
         * | "heart-rate"    | [""]                   | ["heart-rate"]     |
         * | "blood-pressure"| ["systolic"]           | ["systolic"]       |
         */
        const type = filteredData.at(0)?.type;
        const alertCodes =
          (dataPoint.alert?.codes?.length ?? 0) > 1
            ? (dataPoint.alert?.codes ?? [])
            : [type];

        // Obs w alerts
        if (hasAlert && alertCodes.some((ac) => ac === type)) {
          return false;
        }
        // No alerts: Systolic and standard observations
        if (index === 0) {
          return 'circle';
        }
        // No alerts: Diastolic
        return 'triangle';
      };

      return {
        data: filteredData.map((dataPoint) => ({
          y: dataPoint.value,
          x: new Date(dataPoint.date ?? 0).getTime(),
          pointStyle: determinePointStyle(dataPoint),
        })),
        pointStyle: 'circle',
        pointRadius: index === 0 ? 5 : 7,
        label:
          common in VITAL_SIGN_TYPES
            ? formatMessage({ id: VITAL_SIGN_TYPES[common].messageId })
            : FRIENDLY_CODE_NAME_MAP[common],
      };
    });
  }

  const displayCustomTooltip = (
    tooltipItem: ChartJs.TooltipItem<'line'>
  ): string | undefined => {
    const label = tooltipItem.dataset.label;
    const value = formatChartValue
      ? formatChartValue(Number(tooltipItem.parsed.y))
      : tooltipItem.formattedValue;

    if (label === 'Stress') {
      const val = Number(value);
      let stressStatus = formatMessage(messages.normal);

      switch (true) {
        case val <= 50:
          stressStatus = formatMessage(messages.low);
          break;
        case val > 150 && val <= 500:
          stressStatus = formatMessage(messages.elevated);
          break;
        case val > 700:
          stressStatus = formatMessage(messages.high);
          break;
        case val > 50 && val <= 150:
        default:
          break;
      }
      return `${label}: ${stressStatus}`;
    }
    return `${label}: ${value}`;
  };

  const unit = signType?.unit;
  const conversionKey = JSON.stringify(conversion);
  const parameterKey = JSON.stringify(normalRange?.parameters);
  const normalRanges = React.useMemo(() => {
    if (!normalRange?.parameters.length) {
      return [];
    }
    return normalRange.parameters.reduce<
      { lower: number; upper: number; unit: string }[]
    >((acc, curr) => {
      const [lowerLimit, upperLimit] = getBoundaryValues(curr);
      acc.push({
        lower: Number(
          convertUnit(
            lowerLimit,
            unit,
            conversion[code] ? 'CON' : 'SI',
            signType?.precision,
            code
          )
        ),
        upper: Number(
          convertUnit(
            upperLimit,
            unit,
            conversion[code] ? 'CON' : 'SI',
            signType?.precision,
            code
          )
        ),
        unit: getUnit(unit, conversion[code] ? 'CON' : 'SI', code),
      });
      return acc;
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, conversionKey, parameterKey, signType?.precision, unit]);

  const plugins = React.useMemo(() => {
    const normalRangePlugin: Plugin = {
      // id: pluginId,
      id: 'normal-range-plugin',
      beforeDraw: (chart: ChartJs.Chart<'line', ChartJs.Point[]>) => {
        const { config, ctx } = chart;
        const normRanges =
          config.options?.plugins?.['normal-range-plugin']?.range;
        if (!normRanges) {
          return;
        }
        const yAxis = chart.scales['y'];
        const xAxis = chart.scales['x'];
        const { min, max } = yAxis;

        for (const range of normRanges) {
          const { lower, upper } = range;
          const low = lower || min;
          const high = upper || max;

          const yAxisLowerSegment = low <= min ? min : low;
          const yAxisUpperSegment = high >= max ? max : high;

          const lowerLimitPos = yAxis.getPixelForValue(yAxisLowerSegment);
          const upperLimitPos = yAxis.getPixelForValue(yAxisUpperSegment);

          ctx.save();
          ctx.fillStyle = palette.success.light;

          ctx.fillRect(
            xAxis.left,
            lowerLimitPos,
            xAxis.width,
            upperLimitPos - lowerLimitPos
          );
          ctx.restore();
        }
      },
    };
    const plugins: Plugin[] = [
      ...(options?.showNormalRange ? [normalRangePlugin] : []),
    ];
    return plugins;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.showNormalRange]);

  return (
    <>
      <LineChart
        chartData={chartData}
        plugins={plugins}
        formatValue={formatChartValue}
        options={{
          colors: options?.colors,
          normalRange: options?.showNormalRange ? normalRanges : undefined,
          xTimeUnit: determineXTimeInput(minDate, maxDate),
          customTooltipLabel: displayCustomTooltip,
          formatYAxisTicks: true,
        }}
        interval={interval}
        maxDate={maxDate}
        minDate={minDate}
      />
      <p
        style={{
          textAlign: 'center',
          fontSize: 'xx-small',
          color: options?.colors?.font ?? palette.grey.A700,
        }}
      >
        {formatMessage(messages.timeUnknownDisclaimer)}
      </p>
    </>
  );
}

export default HealthLineChart;
